import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { Box, Checkbox, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZE_OPTIONS } from '../filters/portfolioFilters.config';
import { PORTFOLIO_SELECT_COLUMN_WIDTH } from '../hooks/usePortfolioColumns';
import type { PortfolioTableViewProps } from '../hooks/usePortfolioTableState.types';
import {
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
} from '../styles';
import PortfolioRow from './portfolioRow';
import { PortfoliosTablePageLoadError } from './portfoliosTablePageLoadError';
import { PortfoliosTableSkeletonRow } from './portfoliosTableSkeletonRow';
import {
  PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS,
  PortfolioTradingTableHead,
} from './portfolioTradingTableHead';

export const PortfoliosTableView = ({
  portfolioTable,
  displayedPortfolios,
  rowsToRender,
  ghostReport,
  selectedPortfolios,
  onSelectAll,
  onToggleSelect,
  expandedPortfolioId,
  onSetExpanded,
  densityConfig,
  isPageLoading,
  isPageReady,
  hasPageLoadError,
  pageGraphQLErrors,
  page,
  onPageChange,
  onRetryPage,
  onGoBackPage,
  totalPages,
  totalCount,
  showPagination,
  pageSize,
  onPageSizeChange,
  unresolvedRowCount,
  hasActiveFilters,
  effectiveRenderCount,
  visibleRowCount,
  organizationId,
  bulkTradingProcess,
  onTradeComplete,
  pinnedPortfolioReportId,
  canReadRepCode,
  driftTolerancePercent,
}: PortfolioTableViewProps) => {
  const { t } = useTranslation('portfolioTradingHub');
  const tableColSpan = portfolioTable.getAllLeafColumns().length;

  return (
    <>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <PortfolioTradingTableHead
          table={portfolioTable}
          skipColumnIds={PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS.portfolios}
          dataHeaderCellSx={{
            padding: densityConfig.cellPadding,
            color: SECTION_HEADER_SX.color,
            fontWeight: SORTABLE_HEADER_SX.fontWeight,
            cursor: SORTABLE_HEADER_SX.cursor,
            userSelect: SORTABLE_HEADER_SX.userSelect,
            textTransform: 'none',
            '&:hover': SORTABLE_HEADER_SX['&:hover'],
            '& .MuiTypography-root': {
              fontSize: '12px',
              letterSpacing: 0,
              textTransform: 'none',
            },
          }}
          leadingCells={[
            {
              key: 'select',
              sx: {
                width: PORTFOLIO_SELECT_COLUMN_WIDTH,
                padding: densityConfig.cellPadding,
                paddingLeft: 0,
                paddingRight: 0,
              },
              content: (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Checkbox
                    checked={
                      selectedPortfolios.size === displayedPortfolios.length &&
                      displayedPortfolios.length > 0
                    }
                    onChange={(event) => onSelectAll(event.target.checked)}
                  />
                </Box>
              ),
            },
          ]}
        />
        <TableBody
          sx={{
            opacity: isPageLoading ? 0.5 : 1,
            pointerEvents: isPageLoading ? 'none' : 'auto',
            transition: 'opacity 0.15s',
          }}
        >
          {displayedPortfolios.length === 0 && isPageLoading && (
            <PortfoliosTableSkeletonRow colSpan={tableColSpan} />
          )}
          {displayedPortfolios.length === 0 &&
            isPageReady &&
            hasPageLoadError && (
              <PortfoliosTablePageLoadError
                colSpan={tableColSpan}
                page={page}
                pageGraphQLErrors={pageGraphQLErrors}
                onRetryPage={onRetryPage}
                onGoBackPage={onGoBackPage}
              />
            )}
          {displayedPortfolios.length === 0 &&
            isPageReady &&
            !hasPageLoadError && (
              <TableRow>
                <TableCell colSpan={tableColSpan}>
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...SECONDARY_TEXT_SX,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    {unresolvedRowCount > 0
                      ? t('portfoliosTable.unresolvedRows', {
                          count: unresolvedRowCount,
                        })
                      : t('portfoliosTable.noPortfoliosFound')}{' '}
                    {unresolvedRowCount === 0 &&
                      (hasActiveFilters
                        ? t('portfoliosTable.adjustFiltersHint')
                        : t('portfoliosTable.noDataAvailable'))}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          {ghostReport && (
            <PortfolioRow
              key={`ghost-${ghostReport.id}`}
              report={ghostReport}
              isPinned
              selected={false}
              onToggleSelect={() => {}}
              isExpanded={expandedPortfolioId === ghostReport.id}
              onSetExpanded={onSetExpanded}
              organizationId={organizationId}
              bulkTradingProcess={bulkTradingProcess}
              onTradeComplete={onTradeComplete}
              colSpan={tableColSpan}
              canReadRepCode={canReadRepCode}
              driftTolerancePercent={driftTolerancePercent}
            />
          )}
          {rowsToRender.map((row) => {
            const report = row.original;
            return (
              <PortfolioRow
                key={report.id}
                report={report}
                isPinned={pinnedPortfolioReportId === report.id}
                selected={selectedPortfolios.has(report.id)}
                onToggleSelect={onToggleSelect}
                isExpanded={expandedPortfolioId === report.id}
                onSetExpanded={onSetExpanded}
                organizationId={organizationId}
                bulkTradingProcess={bulkTradingProcess}
                onTradeComplete={onTradeComplete}
                colSpan={tableColSpan}
                canReadRepCode={canReadRepCode}
                driftTolerancePercent={driftTolerancePercent}
              />
            );
          })}
          {(isPageLoading || effectiveRenderCount < visibleRowCount) && (
            <PortfoliosTableSkeletonRow colSpan={tableColSpan} py={1} />
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Pagination
            count={Math.max(totalPages, 1)}
            page={Math.min(page, Math.max(totalPages, 1))}
            onChange={(_: unknown, newPage: number) => onPageChange(newPage)}
            perPage={pageSize}
            onChangePerPage={onPageSizeChange}
            showPerPage
            pageSizes={[...PAGE_SIZE_OPTIONS]}
            disabled={isPageLoading && totalCount === 0}
          />
        </Box>
      )}
    </>
  );
};
